import { SiteLink } from "@i18n/SiteLink";
import { cn } from "@ui/lib";
import type { PropsWithChildren, ReactNode } from "react";

/**
 * Bento layout primitives for marketing pages. The grid is a plain 6-column
 * CSS grid; tiles claim spans with the `span` presets so layouts stay on the
 * shared rhythm instead of arbitrary col-spans. Visual content lives inside
 * the tile; the optional heading/body caption renders beneath it, matching
 * the reference blocks' tile-then-caption pattern.
 */

export function BentoGrid({
	className,
	children,
}: PropsWithChildren<{ className?: string }>) {
	return (
		<div
			className={cn(
				"grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6",
				className,
			)}
		>
			{children}
		</div>
	);
}

const SPANS = {
	/** One third of the row on desktop. */
	third: "lg:col-span-2",
	/** Half the row on desktop. */
	half: "lg:col-span-3 sm:col-span-2",
	/** Two thirds of the row on desktop. */
	twoThirds: "lg:col-span-4 sm:col-span-2",
	/** The full row. */
	full: "lg:col-span-6 sm:col-span-2",
} as const;

export type BentoSpan = keyof typeof SPANS;

export function BentoTile({
	span = "third",
	heading,
	body,
	className,
	tileClassName,
	children,
}: PropsWithChildren<{
	span?: BentoSpan;
	heading?: string;
	body?: string;
	className?: string;
	/** Extra classes for the visual card itself (padding overrides, etc.). */
	tileClassName?: string;
}>) {
	return (
		<div className={cn("flex min-w-0 flex-col", SPANS[span], className)}>
			<div
				className={cn(
					"flex-1 overflow-hidden rounded-2xl border bg-card p-5",
					tileClassName,
				)}
			>
				{children}
			</div>
			{heading || body ? (
				<div className="mt-4 px-1">
					{heading ? (
						<h3 className="text-base font-bold tracking-tight">
							{heading}
						</h3>
					) : null}
					{body ? (
						<p className="mt-1.5 text-sm text-muted-foreground">
							{body}
						</p>
					) : null}
				</div>
			) : null}
		</div>
	);
}

/** Eyebrow + headline + lede, shared by every marketing section. */
export function SectionHeader({
	eyebrow,
	title,
	lede,
	align = "center",
	className,
}: {
	eyebrow?: string;
	title: string;
	lede?: string;
	align?: "center" | "left";
	className?: string;
}) {
	return (
		<div
			className={cn(
				"max-w-2xl",
				align === "center" ? "mx-auto text-center" : "",
				className,
			)}
		>
			{eyebrow ? (
				<p className="text-sm font-bold uppercase tracking-wide text-primary">
					{eyebrow}
				</p>
			) : null}
			<h2 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">
				{title}
			</h2>
			{lede ? (
				<p className="mt-3 text-lg text-muted-foreground">{lede}</p>
			) : null}
		</div>
	);
}

/** Full-width cross-link band: one message, one arrow, one destination.
 *  With `href` the label is a real link; without it (mockups) it stays
 *  an inert span. */
export function LinkBand({
	message,
	linkLabel,
	href,
	className,
	children,
}: PropsWithChildren<{
	message: string;
	linkLabel: string;
	href?: string;
	className?: string;
}>) {
	const label = `${linkLabel} →`;
	return (
		<div
			className={cn(
				"flex flex-col items-start justify-between gap-4 rounded-2xl border bg-muted/50 px-6 py-5 sm:flex-row sm:items-center",
				className,
			)}
		>
			<p className="text-base font-medium">{message}</p>
			<span className="flex shrink-0 items-center gap-4">
				{children}
				{href ? (
					<SiteLink
						href={href}
						className="text-sm font-bold text-primary hover:underline"
					>
						{label}
					</SiteLink>
				) : (
					<span className="text-sm font-bold text-primary">
						{label}
					</span>
				)}
			</span>
		</div>
	);
}

/** Marketing section wrapper: consistent vertical rhythm + container. */
export function Section({
	id,
	muted,
	className,
	children,
}: PropsWithChildren<{ id?: string; muted?: boolean; className?: string }>) {
	return (
		<section id={id} className={cn(muted && "bg-muted/30", className)}>
			<div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
				{children}
			</div>
		</section>
	);
}

/** Primary/secondary CTA pair used in heroes and closing bands. With hrefs
 *  the buttons are real links; without them (mockups) inert spans. */
export function CtaPair({
	primary,
	primaryHref,
	secondary,
	secondaryHref,
	className,
}: {
	primary: string;
	primaryHref?: string;
	secondary?: string;
	secondaryHref?: string;
	className?: string;
}) {
	const primaryClass =
		"rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-sm";
	const secondaryClass =
		"rounded-lg border bg-background px-5 py-2.5 text-sm font-bold text-foreground";
	return (
		<div className={cn("flex flex-wrap items-center gap-3", className)}>
			{primaryHref ? (
				<SiteLink href={primaryHref} className={primaryClass}>
					{primary}
				</SiteLink>
			) : (
				<span className={primaryClass}>{primary}</span>
			)}
			{secondary ? (
				secondaryHref ? (
					<SiteLink href={secondaryHref} className={secondaryClass}>
						{secondary}
					</SiteLink>
				) : (
					<span className={secondaryClass}>{secondary}</span>
				)
			) : null}
		</div>
	);
}

/** Small uppercase chip used for tier labels and stage names. */
export function TierChip({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	return (
		<span
			className={cn(
				"inline-flex items-center rounded-full border bg-background px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground",
				className,
			)}
		>
			{children}
		</span>
	);
}
